import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import ContentNetworkLayer from '../../helpers';
import {
  ARRANGEINORDER,
  DRAGDROP,
  FIB,
  MCQ,
  MULTISELECT,
  SWIPELEFTRIGHT,
  TRUE_FALSE,
} from '../../globals/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAnswers,
  setAnswers,
  setQuestion,
  getQuestion,
  setCurrentSubject,
} from '../../globals/application';
import { subjectUsesMath } from '../EquationModal';
import Question from '../QuestionTypes/Question';
import {
  ArrangeInOrder,
  DragAndDrop,
  MultipleChoice,
  MultiSelect,
  SwipeLeftRight,
  TrueOrFalse,
} from '../QuestionTypes';

const TYPE_LABELS = {
  [MCQ]: 'Multiple Choice',
  [ARRANGEINORDER]: 'Arrange In Order',
  [FIB]: 'Fill In The Blanks',
  [TRUE_FALSE]: 'True / False',
  [MULTISELECT]: 'Multi-Select',
  [SWIPELEFTRIGHT]: 'Swipe Left / Right',
  [DRAGDROP]: 'Drag and Drop',
};

const TYPE_MAP = {
  [MCQ]: 'MCQ',
  [MULTISELECT]: 'MultiSelect',
  [TRUE_FALSE]: 'True/False',
  [FIB]: 'FIB',
  [ARRANGEINORDER]: 'Matching',
  [SWIPELEFTRIGHT]: 'SwipeLeftRight',
  [DRAGDROP]: 'DragDrop',
};

const HINTS = {
  [FIB]: 'Type "blank" where you want the answer inserted. e.g. "The capital of France is blank."',
  [ARRANGEINORDER]: 'Enter items in the correct order — they will be randomized for the student.',
  [SWIPELEFTRIGHT]:
    'Student will swipe the question card left or right. Customize the labels for each direction.',
  [DRAGDROP]:
    'Choose Reorder mode (arrange items in order) or Categorize mode (sort items into categories).',
};

function answersValid(questionType, answers, questionText) {
  if (!answers && answers !== false) return false;
  if (questionType === TRUE_FALSE) return answers === 'true' || answers === 'false';
  if (questionType === SWIPELEFTRIGHT) {
    return answers?.correctAnswer === 'left' || answers?.correctAnswer === 'right';
  }
  if (questionType === DRAGDROP) {
    if (!answers?.mode) return false;
    if (answers.mode === 'reorder') {
      return answers.items?.length >= 2 && answers.items.every(it => it.text?.trim());
    }
    if (answers.mode === 'categorize') {
      return (
        answers.zones?.length >= 2 &&
        answers.zones.every(z => z.label?.trim()) &&
        answers.items?.length >= 2 &&
        answers.items.every(it => it.text?.trim())
      );
    }
    return false;
  }
  if (!Array.isArray(answers) || answers.length === 0) return false;
  if (questionType === MCQ || questionType === FIB) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === MULTISELECT) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === ARRANGEINORDER) {
    return answers.length >= 2 && answers.every(a => a.ans?.trim());
  }
  return true;
}

export default function AddTopic() {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);
  const question = useSelector(getQuestion);

  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState('');
  const [name, setName] = useState('');
  const [topicId, setTopicId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Topic Questions state
  const [questionType, setQuestionType] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [topicQuestions, setTopicQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Check if selected subject uses math symbols
  const selectedSubject = useMemo(
    () => subjects.find(s => s.code === parseInt(subject)),
    [subjects, subject]
  );
  const showEquationButton = useMemo(
    () => subjectUsesMath(selectedSubject?.name),
    [selectedSubject]
  );

  // Clear answers + question text when type changes
  useEffect(() => {
    dispatch(setAnswers([]));
    dispatch(setQuestion(''));
  }, [questionType, dispatch]);

  const isTopicValid = subject && name.trim();

  const isQuestionValid =
    questionType &&
    question.trim() &&
    answersValid(questionType, answers, question) &&
    (questionType !== FIB || question.toLowerCase().includes('blank'));

  const fibMissingBlank =
    questionType === FIB && question.trim() && !question.toLowerCase().includes('blank');

  useEffect(() => {
    ContentNetworkLayer.getSubjects()
      .then(res => setSubjects(res.subjects))
      .catch(() => toast.error('Could not load subjects.'));
  }, []);

  // Load topic questions when topicId changes
  useEffect(() => {
    if (topicId) {
      setLoadingQuestions(true);
      ContentNetworkLayer.getTopicQuestions(topicId)
        .then(res => setTopicQuestions(res || []))
        .catch(() => {
          toast.error('Could not load topic questions.');
          setTopicQuestions([]);
        })
        .finally(() => setLoadingQuestions(false));
    }
  }, [topicId]);

  const handleFile = f => {
    setFileName(f.name);
    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = () => setFile(reader.result);
    reader.onerror = () => toast.error('Could not read file.');
  };

  const handleCreateTopic = async e => {
    e.preventDefault();
    if (!isTopicValid) return;
    setLoading(true);
    try {
      const response = await ContentNetworkLayer.createTopic({
        name: name.trim(),
        code: parseInt(subject),
      });
      toast.success('Topic added successfully!');

      // Set topic ID from response to enable topic questions section
      if (response.topicId) {
        setTopicId(response.topicId);
      }

      setName('');
      setSubject('');
    } catch (error) {
      toast.error('Failed to add topic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTopicQuestion = async e => {
    e.preventDefault();
    if (!isQuestionValid || !topicId) return;
    setQuestionLoading(true);

    const question_id = Math.floor(100000 + Math.random() * 900000);

    const payload = {
      question_text: question,
      question_id,
      question_type: questionType,
      topic_id: topicId,
    };

    // True/False: backend reads `answer` field, not `answers`
    if (questionType === TRUE_FALSE) {
      payload.answer = answers;
    } else if (questionType === DRAGDROP) {
      // Backend expects snake_case: item_text, correct_position, zone_id, zone_label
      const dragDropItems = answers.items.map((item, index) => {
        const zone = answers.zones.find(z => z.id === item.zoneId);
        return {
          item_text: item.text,
          correct_position: index + 1,
          zone_id: item.zoneId,
          zone_label: zone?.label || '',
        };
      });
      payload.drag_drop_items = JSON.stringify(dragDropItems);
    } else {
      payload.answers = JSON.stringify(answers);
    }

    if (file) {
      payload.file = file;
      payload.file_id = Math.floor(100000 + Math.random() * 900000);
      payload.filename = `topic-question-image-${question_id}`;
    }

    try {
      await ContentNetworkLayer.createTopicQuestion(payload);
      toast.success('Topic question added!');
      setQuestionType('');
      dispatch(setQuestion(''));
      dispatch(setAnswers([]));
      setFile(null);
      setFileName('');

      // Reload topic questions
      if (topicId) {
        const updatedQuestions = await ContentNetworkLayer.getTopicQuestions(topicId);
        setTopicQuestions(updatedQuestions || []);
      }
    } catch (error) {
      toast.error('Failed to add topic question. Please try again.');
    } finally {
      setQuestionLoading(false);
    }
  };

  const handleDeleteTopicQuestion = async questionId => {
    if (!window.confirm('Delete this topic question?')) return;

    try {
      await ContentNetworkLayer.deleteTopicQuestion(questionId);
      toast.success('Topic question deleted!');
      setTopicQuestions(topicQuestions.filter(q => q.questionId !== questionId));
    } catch (error) {
      toast.error('Failed to delete topic question.');
    }
  };

  return (
    <div className="form-card">
      <div className="form-card-header">
        <h2>Add Topic</h2>
        <p>Create a topic under an existing subject and optionally add questions.</p>
      </div>

      <form onSubmit={handleCreateTopic}>
        <div className="field-group">
          <label className="field-label">Subject</label>
          <select
            className="field-select"
            value={subject}
            onChange={e => setSubject(e.target.value)}
          >
            <option value="">Select a subject...</option>
            {subjects.map(s => (
              <option key={s.code} value={s.code}>
                {s.name} — {s.level}
              </option>
            ))}
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">Topic Name</label>
          <input
            className="field-input"
            type="text"
            placeholder="e.g. Algebra"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>

        <div className="form-footer">
          <span />
          <div className="form-footer-actions">
            <button type="submit" className="btn btn-primary" disabled={!isTopicValid || loading}>
              {loading ? 'Adding...' : 'Add Topic'}
            </button>
          </div>
        </div>
      </form>

      {/* Topic Questions Section */}
      {topicId && (
        <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '1px solid #e5e7eb' }}>
          <div className="form-card-header">
            <h2>Topic Questions</h2>
            <p>Add quiz questions specific to this topic (Learn mode only).</p>
          </div>

          {/* Topic Questions List */}
          {loadingQuestions ? (
            <p style={{ color: '#666' }}>Loading topic questions...</p>
          ) : topicQuestions.length > 0 ? (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                Existing Topic Questions ({topicQuestions.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {topicQuestions.map(q => (
                  <div
                    key={q.questionId}
                    style={{
                      padding: '12px',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'start',
                    }}
                  >
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>
                        {q.questionText}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                        Type: {q.questionType}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteTopicQuestion(q.questionId)}
                      style={{
                        padding: '4px 8px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Add Topic Question Form */}
          <form onSubmit={handleCreateTopicQuestion}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
              Add New Topic Question
            </h3>

            <div className="field-row">
              <div className="field-group">
                <label className="field-label">Question Type</label>
                <select
                  className="field-select"
                  value={questionType}
                  onChange={e => setQuestionType(e.target.value)}
                >
                  <option value="">Select type...</option>
                  {Object.entries(TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {HINTS[questionType] && (
              <div className="info-banner">
                <span className="info-icon">💡</span>
                <span>{HINTS[questionType]}</span>
              </div>
            )}

            {fibMissingBlank && (
              <div
                className="info-banner"
                style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
              >
                <span className="info-icon">⚠️</span>
                <span>Add the word "blank" in your question where the answer should go.</span>
              </div>
            )}

            {questionType && <Question showEquationButton={showEquationButton} />}

            {questionType === MCQ || questionType === FIB ? (
              <MultipleChoice showEquationButton={showEquationButton} />
            ) : questionType === MULTISELECT ? (
              <MultiSelect showEquationButton={showEquationButton} />
            ) : questionType === ARRANGEINORDER ? (
              <ArrangeInOrder showEquationButton={showEquationButton} />
            ) : questionType === TRUE_FALSE ? (
              <TrueOrFalse />
            ) : questionType === SWIPELEFTRIGHT ? (
              <SwipeLeftRight />
            ) : questionType === DRAGDROP ? (
              <DragAndDrop showEquationButton={showEquationButton} />
            ) : null}

            <div className="form-footer">
              <span />
              <div className="form-footer-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isQuestionValid || questionLoading}
                >
                  {questionLoading ? 'Adding...' : 'Add Topic Question'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
